import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

// Lifecycle event codes from docs/ai-knowledge/lifecycle-events.md.
// Listed here so the EventCode filter can render as a multi-select instead
// of a free-text textbox — admin should never have to remember the exact
// snake_case names.
export const EVENT_CODES = [
  'requisition_approved',
  'job_published',
  'candidate_applied',
  'offer_accepted',
  'preboarding_started',
  'preboarding_completed',
  'onboarding_started',
  'period_30_completed',
  'period_60_completed',
  'onboarding_completed',
  'offboarding_initiated',
  'it_access_revoked',
  'offboarding_completed',
  'alumni_created',
  'military_return',
  'boomerang_application',
  // Cross-cutting
  'kvkk_consent_revoked',
];

const STATE_OPTIONS = [
  { id: 'pending', labelKey: 'lifecycleEvent.state.pending' },
  { id: 'processed', labelKey: 'lifecycleEvent.state.processed' },
  { id: 'dlq', labelKey: 'lifecycleEvent.state.dlq' },
];

const useLifecycleEventsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/lifecycle-events?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const retryDlq = useCallback(async (id) => {
    try {
      await api.post(`/v1/lifecycle-events/${id}/retry`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const getDetail = useCallback(async (id) => {
    const res = await api.get(`/v1/lifecycle-events/${id}`);
    return res.data?.data;
  }, []);

  // ── Columns ────────────────────────────────────────────────────────────
  // Type-aware filtering as required by CLAUDE.md memory rule:
  //   - Date columns use dateFilterColumn (gte/lte/between)
  //   - Numeric columns use numericFilterColumn (gte/lte/between)
  //   - Enum columns use 'in' operator with multi-select
  //   - String columns default to 'like' textField
  const columns = useMemo(() => {
    const stateLabel = (s) => {
      switch (s) {
        case 'processed': return t('lifecycleEvent.state.processed');
        case 'dlq': return t('lifecycleEvent.state.dlq');
        case 'pending':
        default: return t('lifecycleEvent.state.pending');
      }
    };

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'eventCode',
        label: t('lifecycleEvent.eventCode'),
        options: {
          filter: true,
          sort: true,
          filterType: 'custom',
          filterOptions: {
            defaultOp: 'in',
            display: (filterList, onChange, index, column) => {
              const current = filterList[index] && filterList[index][0]
                ? filterList[index][0].split(',').filter(Boolean)
                : [];
              return (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>{t('lifecycleEvent.eventCode')}</InputLabel>
                  <Select
                    multiple
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value;
                      const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
                      filterList[index] = csv ? [csv] : [];
                      onChange(filterList[index], index, column);
                    }}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {EVENT_CODES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            },
          },
        },
      },
      {
        name: 'state',
        label: t('lifecycleEvent.state.label'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          // Backend derives state from ProcessedAt + DlqAt; this column
          // is computed client-side from the DTO's `derivedState` field.
          customBodyRender: (_, tableMeta) => {
            const state = data[tableMeta.rowIndex]?.derivedState || 'pending';
            return stateLabel(state);
          },
          filterOptions: {
            defaultOp: 'in',
            display: (filterList, onChange, index, column) => {
              const current = filterList[index] && filterList[index][0]
                ? filterList[index][0].split(',').filter(Boolean)
                : [];
              return (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>{t('lifecycleEvent.state.label')}</InputLabel>
                  <Select
                    multiple
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value;
                      const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
                      filterList[index] = csv ? [csv] : [];
                      onChange(filterList[index], index, column);
                    }}
                    renderValue={(selected) => selected.map((s) => stateLabel(s)).join(', ')}
                  >
                    {STATE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>{t(opt.labelKey)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            },
          },
        },
      },
      dateFilterColumn('occurredAt', t('lifecycleEvent.occurredAt'), lang, { includeTime: true }),
      numericFilterColumn('retryCount', t('lifecycleEvent.retryCount'), { decimals: 0 }),
      {
        name: 'idempotencyKey',
        label: t('lifecycleEvent.idempotencyKey'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '-',
        },
      },
      dateFilterColumn('processedAt', t('lifecycleEvent.processedAt'), lang, { includeTime: true }),
      dateFilterColumn('dlqAt', t('lifecycleEvent.dlqAt'), lang, { includeTime: true }),
      {
        name: 'lastError',
        label: t('lifecycleEvent.lastError'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => v ? <span style={{ color: '#E53935' }}>{v}</span> : '-',
        },
      },
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, retryDlq, getDetail };
};

export default useLifecycleEventsApi;
